import config from '../config';
import {
  authHeader,
  customFetch,
  jsonHeader,
  handleResponse,
  commonRequestOptions,
  subjects,
} from '../helpers';

const endpointPath = config.api.API_URL + '/entity';

export const entityService = {
  getAll,
  getById,
  getAncestors,
  getByGlossaryId,
  getByName,
  create,
  update,
  checkDestroy,
  destroy,
};

function getAll(signal) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
    ...commonRequestOptions(),
  };
  return customFetch(`${endpointPath}/all`, requestOptions, signal).then(handleResponse);
}

function getById(id, signal) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
    ...commonRequestOptions(),
  };
  const entityRequest = customFetch(`${endpointPath}/${id}`, requestOptions, signal).then(
    handleResponse
  );

  // Fetch the entity and its ancestor chain together and publish currentEntity and
  // currentEntityAncestors in the same render. Doing both in one update is what prevents the
  // breadcrumb parent crumbs from flashing in a step after the entity crumb. The ancestors
  // fetch is non-critical, so its failure must not reject the entity load (a rejecting entity request
  // still propagates, preserving the view's not-found handling).
  return Promise.all([
    entityRequest,
    getAncestors(id, signal).catch(() => ({ entities: [] })),
  ]).then(([res, ancestorsRes]) => {
    subjects.currentEntity.next(res.entities[0] || { entity_id: id });
    subjects.currentGlossary.next(
      res.entities[0]
        ? {
            glossary_id: res.entities[0].glossary_id,
            glossary_name: res.entities[0].glossary_name,
          }
        : 'not_found'
    );
    subjects.currentEntityAncestors.next({
      entity_id: id,
      ancestors: ancestorsRes.entities,
    });
    return res;
  });
}

/**
 * Sends a GET request for an entity's ancestor chain (parent entities), ordered
 * root -> immediate parent. Returns an empty list for a top-level entity.
 * @param {number|string} id - The entity id.
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ entities: object[] }>}
 */
function getAncestors(id, signal) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
    ...commonRequestOptions(),
  };
  return customFetch(`${endpointPath}/${id}/ancestors`, requestOptions, signal).then(
    handleResponse
  );
}

function getByGlossaryId(id, signal) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
    ...commonRequestOptions(),
  };
  return customFetch(`${endpointPath}/glossary/${id}`, requestOptions, signal).then(handleResponse);
}

/**
 * Sends a GET request for entities matching a name, compared case-insensitively.
 * @param {string} name - URL-encoded name to match.
 * @param {number} [id] - Entity to exclude from the result; omit to exclude none.
 * @param {number} [glossaryId] - Restrict the search to this glossary, which is the scope name
 *   uniqueness is enforced over. Omit to search all of Katalogue.
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ entities: Entity[] }>}
 */
function getByName(name, id, glossaryId, signal) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
    ...commonRequestOptions(),
  };
  const query = glossaryId ? `?glossary_id=${glossaryId}` : '';
  return customFetch(`${endpointPath}/name/${name}/${id}${query}`, requestOptions, signal).then(
    handleResponse
  );
}

function create(items, signal) {
  const requestOptions = {
    method: 'POST',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}`, requestOptions, signal).then(handleResponse);
}

function update(items, signal) {
  const requestOptions = {
    method: 'PUT',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}`, requestOptions, signal).then(handleResponse);
}

function checkDestroy(items, signal) {
  const requestOptions = {
    method: 'POST',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}/check_delete`, requestOptions, signal).then(handleResponse);
}

function destroy(items, signal) {
  const requestOptions = {
    method: 'DELETE',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}`, requestOptions, signal).then(handleResponse);
}
